import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@xstyled/styled-components';
import { select } from '~/lib/select';
import { useTrainingStore } from '../state/useTrainingStore';
import { TrainingState } from '../state/TrainingState';
import { t } from 'ttag';

/**
 * Figma EDS Timer (854:1969): rgba(46,46,46,0.6), h 38, radius 8, px 16.
 * Settings HUD is `right: 8` + 38 wide; gap 16 → pill `right: 62`.
 * Light + dark use the same chrome (854:3858 / 854:4022).
 */
const Pill = styled.div`
  position: fixed;
  top: 20px;
  right: 62px;
  z-index: hud;
  box-sizing: border-box;
  background: rgba(46, 46, 46, 0.6);
  padding: 0 16px;
  height: 38px;
  min-height: 38px;
  border-radius: md;
  color: ${p => p.theme.colors.white};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-variant-numeric: lining-nums proportional-nums;
  white-space: nowrap;

  /* Keep 16px gap when Settings button shifts (OpenSettingsButton media). */
  @media ((max-width: 580px) and (max-height: 300px)) {
    right: 64px;
  }
  @media (max-height: 220px) {
    top: 8px;
    right: 64px;
  }
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'remainingDurationInMinutesForCourseTimers',
    'remainingDurationInSecondsForCourseTimers',
    'shouldDisplayTimeRemainingPill',
    'reviewTimerPaused'
  );

const getHours = (time: number) => Math.floor(time / 60);
const getMinutes = (time: number) => time % 60;

export const TimeRemainingPill: React.FC = () => {
  const {
    remainingDurationInMinutesForCourseTimers: remainingDurationInMinutes,
    remainingDurationInSecondsForCourseTimers: remainingSeconds,
    shouldDisplayTimeRemainingPill,
    reviewTimerPaused
  } = useTrainingStore(selector);

  if (!shouldDisplayTimeRemainingPill) return null;
  if (remainingDurationInMinutes == null && remainingSeconds == null)
    return null;

  const duration = remainingDurationInMinutes ?? 0;
  const hours =
    remainingSeconds == null
      ? getHours(duration)
      : Math.floor(remainingSeconds / 3600);
  const minutes =
    remainingSeconds == null
      ? getMinutes(duration)
      : Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds == null ? null : remainingSeconds % 60;
  const label =
    remainingSeconds === 0
      ? t`Time requirement met`
      : seconds == null
        ? t`Time remaining: ${hours}h ${minutes}m`
        : t`Time remaining: ${hours}h ${minutes}m ${seconds}s`;

  return createPortal(
    <Pill
      className="timeRemainingPillStyleForDarkTheme"
      data-test-id="time-remaining-pill"
    >
      {reviewTimerPaused && remainingSeconds !== 0 ? t`Timer paused` : label}
    </Pill>,
    document.getElementById('root')!
  );
};
